import React, { useState, useRef, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useReactToPrint } from 'react-to-print'
import { SampleTable, Loading } from "components"
import { Button } from "react-bootstrap"
import { samples, message, analyses } from "state_management"
import useT from "utils/hooks/useT"
import { ConfirmDialog, Popup } from "components"
import { Container } from "components/Form"
import { FiInfo, FiPrinter } from "react-icons/fi"
import { FaLockOpen, FaLock } from "react-icons/fa"
import PlannableSamples from "./PlannableSamples"
import SampleTableForPrinting from "./SampleTableForPrinting"
import isOnMobile from "utils/scripts/isOnMobile"

const PrintComponent = React.forwardRef(({ data }, ref) => {
  // Forward the ref to the printed div
  return (
    <div className="print-container" ref={ref}>
      <SampleTableForPrinting popupSamples={data} />
    </div>
  )
})

export default function SampleTableForPlannedAnalysis({ analysisId, cancelHandler, readOnly = false }) {
  const dispatch = useDispatch()
  const { t } = useT("messages")
  const { t: tc } = useT("common")
  const sampleData = useSelector((state) => state.samples[analysisId] || null)
  const [unplanConfirm, setUnplanConfirm] = useState(false)
  // SPEC-032: non-LIMS teams keep the legacy "Fail Selected" → rerun flow. LIMS teams fail via the
  // QC Results & Approvals / OOS flow instead, so the button below is gated on !showLims.
  const [failConfirm, setFailConfirm] = useState(false)
  const [showUnplannedSamples, setShowUnplannedSamples] = useState(false)
  const [checkedSamples, setCheckedSamples] = useState([])
  const analysis = useSelector((state) => state.analyses.plannedAnalyses[analysisId])
  const showLims = useSelector((state) => state.teamStatus.showLims)
  const hasExclusiveSample = sampleData?.items ? sampleData.items.some(sample => sample.exclusive === true) : false

  // Flag the selected samples (out of trend). Flagging is reversible, so no confirm dialog.
  // SPEC-020 (C5): flag/fail here no longer raise LIMS OOS/OOT register entries — those records
  // had no Phase-1 investigation, no UI and no close path (orphans). A formal OOS is opened ONLY
  // via the investigation modal (FAIL card / QA worklist).
  const handleFlagSelected = () => {
    if (checkedSamples.length === 0) {
      dispatch(message.warning(t("adhoc.noCheckboxesSelected")))
      return
    }
    const selected = (sampleData.items || []).filter((s) => checkedSamples.includes(s.id))
    selected.forEach((s) => dispatch(samples.flagSample(s.id, s.sampleName || "")))
    setCheckedSamples([])
  }

  const printRef = useRef(null)
  const handlePrint = useReactToPrint({
    contentRef: printRef,
  })

  // If the analysis no longer exists (e.g., all samples were unplanned), close the popup
  useEffect(() => {
    if (!analysis && sampleData && !sampleData.isLoading) {
      cancelHandler()
    }
  }, [analysis, sampleData, cancelHandler])

  // Fetch the planned analysis's samples on mount / when the analysis changes. This MUST run in
  // an effect, not during render — dispatching during render previously updated sibling state
  // mid-render and triggered React's "cannot update a component while rendering" warning.
  useEffect(() => {
    if (!sampleData) {
      dispatch(samples.fetchForPlanned(analysisId))
    }
  }, [analysisId, sampleData, dispatch])

  if (!sampleData || sampleData.isLoading) {
    return <Loading />
  }
  
  // If analysis doesn't exist, show loading while closing
  if (!analysis) {
    return <Loading />
  }

  return (
    <>
      {showUnplannedSamples && (
        <Popup className="sampletable-addsamples-popup" onCancel={() => setShowUnplannedSamples(false)}>
          <PlannableSamples analysisId={analysisId} handler={() => setShowUnplannedSamples(false)} />
        </Popup>
      )}

      {failConfirm && (
        <ConfirmDialog
          onCancel={() => {
            setFailConfirm(false)
          }}
          onConfirm={() => {
            dispatch(samples.failSamples(analysisId, checkedSamples))
            setCheckedSamples([])
            setFailConfirm(false)
          }}
        >
          <Container>
            <div className="FailSamples-info-text">
              <FiInfo title={tc("confirmFailSamplesInfo")} />
            </div>
            <h4>{tc("confirmFailSamplesQuestion")}</h4>
          </Container>
        </ConfirmDialog>
      )}
      {unplanConfirm && (
        <ConfirmDialog
          onCancel={() => {
            setUnplanConfirm(false)
          }}
          onConfirm={() => {
            // Check if we're unplanning all samples
            const isUnplanningAllSamples = checkedSamples.length === sampleData.items.length
            
            dispatch(samples.unplanSamples(analysisId,checkedSamples,sampleData.items.filter(sample => checkedSamples.indexOf(sample.id) !== -1)))
              .then(() => {
                // If all samples were unplanned, close the popup
                if (isUnplanningAllSamples) {
                  cancelHandler()
                }
              })
            setCheckedSamples([])
            setUnplanConfirm(false)
          }}
        >
          <Container>
            <div className="FailSamples-info-text">
              <FiInfo title={tc("confirmUnplanSamplesInfo")} />
            </div>
            <h4>{tc("confirmUnplanSamplesQuestion")}</h4>
          </Container>
        </ConfirmDialog>
      )}
      {!readOnly && (
        <div className="add-samples-asset-view">
          {!isOnMobile && (
            <>
              <p style={{ marginRight: "0.5rem" }}>{tc("lockUnlockSamplesLabel")} </p>
              {analysis.samplesLocked ? (
                <FaLock
                  title={tc("unlockSamples")}
                  className="analysis-samples-lock"
                  onClick={() => {
                    dispatch(analyses.setSamplesLocked(analysisId, false));
                  }}
                />
              ) : (
                  <FaLockOpen
                    title={tc("lockSamples")}
                    className="analysis-samples-lock"
                    onClick={() => {
                      dispatch(analyses.setSamplesLocked(analysisId, true));
                    }}
                  />
                )}
            </>
          )}

          <Button
            variant="success"
            size="lg"
            onClick={ () => setShowUnplannedSamples(true) }
          >
            {tc("addSamples")}
          </Button>
        </div>
      )}
      <div className="sample-table-planned-main-container">
        <div className="sample-table-planned">
          <SampleTable popupSamples={sampleData.items} checkedSamples={checkedSamples} setCheckedSamples={readOnly ? null : setCheckedSamples} analysisId={analysisId} />
        </div>
      </div>
      {hasExclusiveSample && (
        <div className="explanation-text">
          {tc("exclusiveSampleIndicator")}
        </div>
      )}
      <div className="sampletable-buttons">
        <Button variant="secondary" onClick={handlePrint}> <FiPrinter /></Button>
        <PrintComponent data={sampleData.items} ref={printRef} />
        <div className="sampletable-cancel">
          <Button
            variant="light"
            onClick={() => {
              cancelHandler()
            }}
          >
            {tc("cancel")}
          </Button>
        </div>
        {!readOnly && (
          <div className="confirm-buttons">
            <Button
              variant="danger"
              onClick={() => {
                // If no checkboxes are clicked
                if(checkedSamples.length === 0) {
                  dispatch(message.warning(t("adhoc.noCheckboxesSelected")))
                  cancelHandler()
                } else {
                    setUnplanConfirm(true)
                  }
              }}
            >
            {isOnMobile ? tc("unplan") : tc("unplanSelected")}
            </Button>
            {!showLims && (
              <Button
                className="sampletable-fail-button"
                onClick={() => {
                  // SPEC-032: fail the selected samples → backend creates a rerun
                  // (DELETE /samples/fail-samples). Non-LIMS only; LIMS teams fail via QC Results.
                  if (checkedSamples.length === 0) {
                    dispatch(message.warning(t("adhoc.noCheckboxesSelected")))
                    cancelHandler()
                  } else {
                    setFailConfirm(true)
                  }
                }}
              >
                {isOnMobile ? tc("fail") : tc("failSelected")}
              </Button>
            )}
            {showLims && (
              <Button
                variant="warning"
                className="sampletable-flag-button"
                onClick={handleFlagSelected}
              >
                {isOnMobile ? tc("flag") : tc("flagSelected")}
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  )
}
